import { BaseViewModel } from "@iot-platform/core";
import { SensorGroups } from "@station/domain/models/sensor-group.model";
import type { Station } from "@station/domain/models/station.model";
import { SensorGroupGetAllUseCase } from "@station/domain/usecases/sensor-group-get-all.usecase";
import type {
    RelatedChartDefinition,
    RelatedChartSelection,
    RelatedChartType,
} from "@station/domain/utils/related-charts.utils";
import {
    RELATED_CHART_DEFINITIONS,
    isRelatedChartAvailable,
    selectRelatedChartData,
} from "@station/domain/utils/related-charts.utils";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class RelatedChartsViewmodel extends BaseViewModel {
    @observable
    sensorGroups: SensorGroups = SensorGroups.empty();

    @observable
    initialLoading = true;

    @observable
    currentStation: Readonly<Station> | null = null;

    constructor(
        @inject(SensorGroupGetAllUseCase)
        private readonly sensorGroupGetAllUseCase: SensorGroupGetAllUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await this.fetchSensorGroups();
        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    setStation(station: Readonly<Station>): void {
        this.currentStation = station;
    }

    definitionOf(
        type: RelatedChartType,
    ): Readonly<RelatedChartDefinition> | undefined {
        return RELATED_CHART_DEFINITIONS.find(
            (definition) => definition.type === type,
        );
    }

    chartDataOf(
        definition: Readonly<RelatedChartDefinition>,
    ): RelatedChartSelection {
        return selectRelatedChartData(
            definition,
            this.sensorGroups.sensorGroups,
        );
    }

    isChartAvailable(definition: Readonly<RelatedChartDefinition>): boolean {
        return isRelatedChartAvailable(
            definition,
            this.sensorGroups.sensorGroups,
        );
    }

    @action
    private async fetchSensorGroups(): Promise<void> {
        if (!this.currentStation) throw new Error("Current station is not set");

        const sensorGroups = await this.sensorGroupGetAllUseCase.execute(
            this.currentStation.id.value,
        );
        runInAction(() => {
            this.sensorGroups = sensorGroups;
        });
    }
}
